import { Thumbnail as BaseThumbnail, DurationBadge } from '@nucleus/sermon-theme-elements';
import { formatSrcSet } from '@nucleus/sermon-theme-elements/src/lib/media';
import { ImageWeb } from '@nucleus/types/media/image';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { cornerStyleInterpolationFactory } from '../lib/cornerStyle';

declare module '@nucleus/post-theme-elements' {
  interface PostThemeElementsTheme {
    PostGridItem?: {
      background?: string;
      color?: string;
      hover?: {
        background?: string;
        shadow?: string;
      };
    };
  }
}

interface PostGridItemProps {
  children?: React.ReactNode;
  className?: string;
  currentlyReadingButton?: React.ReactNode;
  hoverButton?: React.ReactNode;
  image?: ImageWeb;
  isCurrentlyReading?: boolean;
  isImageVisible?: boolean;
  style?: React.CSSProperties;
  to: string;
}

export const PostGridItem = (props: PostGridItemProps): JSX.Element => {
  const isImageVisible = props.isImageVisible !== false;
  const navigate = useNavigate();

  return (
    <Container
      className={props.className}
      style={props.style}
      onClick={(event) => !event.defaultPrevented && navigate(props.to)}
    >
      {isImageVisible && (
        <Link to={props.to}>
          <ThumbnailContainer>
            <Thumbnail
              src={props.image?.src}
              srcSet={formatSrcSet(props.image?.srcSet)}
              blurHash={props.image?.blurHash}
              focalPoint={props.image?.focalPoint}
            />
            {props.isCurrentlyReading ? (
              <>
                <SolidColorOverlay />
                {props.currentlyReadingButton && <ButtonSecondary>{props.currentlyReadingButton}</ButtonSecondary>}
              </>
            ) : (
              <>
                <LinearGradient />
                {props.hoverButton && <Button>{props.hoverButton}</Button>}
              </>
            )}
          </ThumbnailContainer>
        </Link>
      )}
      <Meta>{props.children}</Meta>
    </Container>
  );
};

const Button = styled.div`
  transform: translateY(50%);
  opacity: 0;

  transition-property: transform, opacity;
  transition-duration: 600ms, 500ms;
  transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1), cubic-bezier(0.19, 1, 0.22, 1);
`;

const ButtonSecondary = styled.div``;

const LinearGradient = styled.div`
  background-image: linear-gradient(0deg, #000000, transparent);
  opacity: 0;

  transition: opacity 900ms cubic-bezier(0.23, 1, 0.32, 1);

  pointer-events: none;
`;

const Meta = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding-left: 6px;
  padding-bottom: 6px;
  overflow: hidden;
`;

const Container = styled.div`
  container-type: inline-size;
  cursor: pointer;
  color: ${({ theme }) => theme._postThemeElements.PostGridItem?.color};
  background: ${({ theme }) => theme._postThemeElements.PostGridItem?.background};
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '12px',
    pill: '25px',
  })};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 12.5px;
  aspect-ratio: 340 / 420;
  width: 100%;

  ${Meta}:first-child {
    padding-top: 30px;
  }

  transition:
    background 250ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 250ms cubic-bezier(0.23, 1, 0.32, 1);

  &:hover {
    background: ${({ theme }) => theme._postThemeElements.PostGridItem?.hover?.background};
    box-shadow: ${({ theme }) => theme._postThemeElements.PostGridItem?.hover?.shadow};
  }

  :hover {
    ${Button} {
      transform: translateY(0);
      opacity: 1;
      transition-duration: 600ms, 150ms;
    }

    ${LinearGradient} {
      opacity: 0.9;
    }
  }

  text-decoration: none;
`;

const SolidColorOverlay = styled.div`
  background: #000;
  opacity: 75%;

  pointer-events: none;
`;

const ThumbnailContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '10px',
    pill: '18px',
  })};
  overflow: hidden;

  ${Button}, ${ButtonSecondary} {
    position: absolute;
  }

  ${LinearGradient}, ${SolidColorOverlay} {
    position: absolute;
    inset: 0;
  }

  ${DurationBadge} {
    position: absolute;
    right: 0;
    bottom: 0;
    margin: 10px;
  }
`;

const Thumbnail = styled(BaseThumbnail)`
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '10px',
    pill: '18px',
  })};
`;
